import { MessageBatcher } from 'message-batcher';
import { IssueDetectorResult, NetworkScores } from 'webrtc-issue-detector';
import { inject, injectable } from 'inversify';
import { NetworkMetric } from './analyticsApiClient/types';
import { ClientMetaPayload, ClientMetaProvider } from '../ClientMetaProvider';
import { TOKEN } from '../inversify.tokens';
import AnalyticsApiClient from './analyticsApiClient';
import type { IPeersService } from './Peers';
import { MyPeer } from './MyPeer';
import type { LoggerFactory } from '../types/container';
import Logger from './Logger';
import TransportsStatsProvider from './WebRTCStats/TransportsStatsProvider';
import { serializeError } from 'serialize-error';

const WIDSupportedIssueReasons = [
  'frozen-video-track',
  'network-media-sync-failure',
  'encoder-cpu-throttling',
  'decoder-cpu-throttling',
  'missing-video-stream-data',
  'missing-audio-stream-data',
];

const ROUNDING_FACTOR = 10;

interface ClientMeta extends ClientMetaPayload {
  clientUniqueId: string;
}

interface NodeActiveStreamsStat {
  inboundActiveStreams: number;
  outboundActiveStreams: number;
}

@injectable()
class WIDHandler {
  readonly #networkScoresBatcher: MessageBatcher;

  readonly #webrtcIssuesBatcher: MessageBatcher;

  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.AnalyticsApiClient) private readonly analyticsApiClient: AnalyticsApiClient,
    @inject(TOKEN.Peers) private readonly peers: IPeersService,
    @inject(TOKEN.MyPeer) private readonly myPeer: MyPeer,
    @inject(TOKEN.LoggerFactory) loggerFactory: LoggerFactory,
    @inject(TOKEN.TransportsStatsProvider) private readonly transportsStatsProvider: TransportsStatsProvider,
    @inject(TOKEN.ClientMetaProvider) private readonly clientMetaProvider: ClientMetaProvider,
  ) {
    this.#logger = loggerFactory('IntegrationsService');
    this.#webrtcIssuesBatcher = new MessageBatcher({ MaxBatchSize: 50, MaxDelay: 60000, MinDelay: 30000 });
    this.#webrtcIssuesBatcher
      .on('batch', (reasons: string[]) => {
        this.analyticsApiClient.metrics
          .sendWIDMetrics({
            reasons,
            clientMeta: this.getClientMeta(),
          })
          .catch((error: unknown) => {
            this.#logger.debug('Failed to sendWIDMetrics', {
              error: serializeError(error),
            });
          });
      });

    this.#networkScoresBatcher = new MessageBatcher({ MaxBatchSize: 20, MaxDelay: 60_000, MinDelay: 20_000 });
    this.#networkScoresBatcher
      .on('batch', (metrics: NetworkMetric[]) => {
        this.analyticsApiClient.metrics
          .sendNetworkMetrics({
            metrics: this.getWorstPerDirectionMetric(metrics),
            clientMeta: this.getClientMeta(),
          })
          .catch((error: unknown) => {
            this.#logger.debug('Failed to sendNetworkMetrics', {
              error: serializeError(error),
            });
          });
      });
  }

  handleIssues(issues: IssueDetectorResult): void {
    issues.forEach((issue): void => this.#logger.info('WebRTCIssue', issue));

    const supportedIssues = issues
      .filter((issue): boolean => WIDSupportedIssueReasons.includes(issue.reason));

    if (supportedIssues.length === 0) {
      return;
    }

    supportedIssues
      .forEach((issue): void => this.#webrtcIssuesBatcher.Queue(issue.reason));
  }

  handleNetworkScores(networkScores: NetworkScores): void {
    const {
      inbound,
      outbound,
      statsSamples: { inboundStatsSample, outboundStatsSample },
    } = networkScores;
    const nodeActiveStreamsStat = this.getNodeActiveStreamsStat();
    const transportsStats = this.transportsStatsProvider.getStats();

    if (inbound !== undefined && inboundStatsSample) {
      const metric: NetworkMetric = {
        jitter: Math.round(inboundStatsSample.avgJitter),
        packetLoss: inboundStatsSample.packetsLoss,
        rtt: inboundStatsSample.rtt,
        mos: Math.round(inbound * ROUNDING_FACTOR) / ROUNDING_FACTOR,
        streams: nodeActiveStreamsStat.inboundActiveStreams,
        direction: 'inbound',
        node: this.clientMetaProvider.meta.datacenterName ?? 'unknown node',
        bitrate: transportsStats?.inbound?.bitrate ?? 0,
        timestamp: (new Date()).toISOString(),
        Qa: transportsStats?.inbound?.Qa,
        Qv: transportsStats?.inbound?.Qv,
        Qav: transportsStats?.inbound?.Qav,
      };
      this.#networkScoresBatcher.Queue(metric);
    }

    if (outbound !== undefined && outboundStatsSample) {
      const metric: NetworkMetric = {
        jitter: Math.round(outboundStatsSample.avgJitter),
        packetLoss: outboundStatsSample.packetsLoss,
        rtt: outboundStatsSample.rtt,
        mos: Math.round(outbound * ROUNDING_FACTOR) / ROUNDING_FACTOR,
        streams: nodeActiveStreamsStat.outboundActiveStreams,
        direction: 'outbound',
        node: this.clientMetaProvider.meta.datacenterName ?? 'unknown node',
        bitrate: transportsStats?.outbound?.bitrate ?? 0,
        timestamp: (new Date()).toISOString(),
        Qa: transportsStats?.outbound?.Qa,
        Qv: transportsStats?.outbound?.Qv,
        Qav: transportsStats?.outbound?.Qav,
      };
      this.#networkScoresBatcher.Queue(metric);
    }
  }

  private getNodeActiveStreamsStat(): NodeActiveStreamsStat {
    const inboundActiveStreams = this.peers.hosts()
      .filter((peer) => peer.id !== this.myPeer?.id)
      .reduce((total, peer) => total + peer.getActiveTracksCount(), 0);
    const myPeer = this.myPeer.get();
    const outboundActiveStreams = myPeer?.getActivePublishersCount() ?? 0;

    return {
      inboundActiveStreams,
      outboundActiveStreams,
    };
  }

  private getClientMeta(): ClientMeta {
    return {
      ...this.clientMetaProvider.meta,
      clientUniqueId: this.clientMetaProvider.meta.clUId,
    };
  }

  private getWorstPerDirectionMetric(metrics: NetworkMetric[]): NetworkMetric[] {
    return (['inbound', 'outbound'])
      .map((direction) => this.pickWorstMetric(metrics.filter((metric) => metric.direction === direction)))
      .filter((metric) => metric !== undefined);
  }

  private pickWorstMetric(items: NetworkMetric[]): NetworkMetric | undefined {
    if (!items.length) {
      return undefined;
    }

    const withQav = items.filter((item): item is NetworkMetric & { Qav: number } =>  typeof item.Qav === 'number');

    return withQav.length
      ? withQav.reduce((worst, current) => (current.Qav < worst.Qav ? current : worst))
      : items.reduce((worst, current) => (current.jitter > worst.jitter ? current : worst));
  };
}

export default WIDHandler;
